import React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ExecutedPlaybookStep, SandboxV2CopyStage } from '@chatsdet/types';
import { API_URL } from '../../../../config';
import { ApiError, toApiError } from '../../../../lib/api-error';
import type { WrongOrgInfo } from '../../../components/OrgSwitchPrompt';

/** Context provided by the agent when a step fails */
export type WorkflowFailureContextDto = {
  observedBehavior?: string;
  expectedBehavior?: string;
  reproSteps?: string;
};

/** Result of a single API call within an api_sequence step */
export type ApiSequenceStepResultDto = {
  stepId: string;
  success: boolean;
  statusCode?: number;
  statusText?: string;
  durationMs?: number;
  error?: string;
  timestamp?: string;
  /** The fully resolved URL that was actually requested */
  resolvedUrl?: string;
  /** The HTTP method used */
  method?: string;
  /** Response headers */
  headers?: Record<string, string>;
  /** Response body */
  body?: string | null;
  /** The request body sent */
  requestBody?: string | null;
  /** The request headers sent */
  requestHeaders?: Record<string, string>;
  /** Results of validation assertions run after this step */
  validationResults?: Array<{
    validation: { left: string; operator: string; right?: string; description?: string };
    passed: boolean;
    actualValue?: string;
    expectedValue?: string;
    message?: string;
  }>;
};

/** Serialized tracked variable from the backend variable registry */
export type SerializedTrackedVariableDto = {
  key: string;
  value: string;
  source: 'inputValue' | 'macro' | 'extracted' | 'credential' | 'seed' | 'childFlowOutput';
  macroExpression?: string;
  extractorExpression?: string;
  originNodeId?: string;
};

/** Metadata stored with a workflow run step */
export type WorkflowRunStepMetadataDto = {
  /** Cache execution path for this step */
  cacheOutcome?: 'hit' | 'miss' | 'fallback' | 'skipped';
  /** Why cache replay missed or fell back */
  cacheOutcomeReason?: string;
  /** Which cache path produced a hit */
  cacheHitSource?: 'replay' | 'recovery' | 'wait_shortcut' | 'l2_gap';
  /** True when the hit came from the wait-only shortcut */
  waitShortcut?: boolean;
  /** Whether step-driven (playbook-step) replay was used */
  stepDrivenReplay?: boolean;
  /** Wall-clock ms of the cache replay / L2 playbook session phase */
  replayDurationMs?: number;
  /** Per-playbook-step outcomes from step-driven replay */
  stepOutcomes?: Array<{
    type: 'cached' | 'gap';
    stepOrder: number;
    success: boolean;
    error?: string;
    recoveredVia?: 'recovery' | 'gap';
  }>;
  /**
   * Execution-time snapshot of the playbook that step-driven replay consumed
   * (full hit, or partial replay before agent fallback). Joined with
   * `stepOutcomes` by `order`/`stepOrder` for per-step ✓/✗ display. Absent for
   * pure-agent steps, legacy action-driven hits, and pre-snapshot runs.
   */
  executedPlaybook?: ExecutedPlaybookStep[];
  /**
   * What the agent was told at this step, rendered at execution time (the
   * live node definition may have been edited since the run).
   */
  nodeIntrospection?: string;
  /** Quick summary of compiled entry types */
  compiledEntryTypes?: Array<'cached' | 'gap'>;
  /** S3 key for failure screenshot captured at moment of failure */
  failureScreenshotS3Key?: string;
  /** Failure context provided by the agent */
  failureContext?: WorkflowFailureContextDto;
  /** Reason step was skipped (if applicable) */
  skippedReason?: string;
  /** Individual API call results for api_sequence nodes */
  stepResults?: ApiSequenceStepResultDto[];
  /** Thinking level used for this step (the fast-drive thinking knob) */
  thinkingLevel?: 'low' | 'medium' | 'high';
  /** Variables set during this step's execution */
  variablesSet?: SerializedTrackedVariableDto[];
  /** Per-turn LLM timing and retry data (from agent turn observability) */
  turnStats?: {
    turns: Array<{
      turnNumber: number;
      startedAtMs: number;
      completedAtMs: number;
      durationMs: number;
      llmFirstResponseMs: number | null;
      inputTokens: number;
      outputTokens: number;
      finishReason: string;
    }>;
    retries: {
      staleResponseRetried: boolean;
      streamRetryCount: number;
      errors: Array<{ message: string; type: string; timestampMs: number }>;
    };
    totalDurationMs: number;
  };
  /** True for a `call_flow` node's step — the child ran as its own `workflow_runs` row. */
  isCallFlowStep?: boolean;
  /** The child run's id, when the child started (absent for a config error caught before launch). */
  childRunId?: string | null;
  /** The called workflow's id — combine with `childRunId` to link to the child run. */
  childWorkflowId?: string | null;
  /** The called workflow's name at call time. */
  childWorkflowName?: string | null;
  /**
   * Why a `call_flow` step failed. `not-run` means this step could not even
   * start the child (no flow selected, an unresolved `$var`, an input the
   * child no longer declares) — an authoring problem, not a test result.
   * `child-failed` means the child ran and failed its own assertions.
   */
  failureClass?: 'not-run' | 'child-failed';
  /** Child's declared seed outputs that were actually produced and published to this flow. */
  publishedOutputs?: string[];
  /** Child's declared seed outputs that were NOT produced — a broken export in the child. */
  missingOutputs?: string[];
  /** True when the child flow failed but `onFailure: 'warn'` kept this step green. */
  warnedFailure?: boolean;
  [key: string]: unknown;
};

export type WorkflowRunStepDto = {
  id: string;
  runId: string;
  nodeId: string;
  status: 'pending' | 'running' | 'waiting' | 'success' | 'failed' | 'inconclusive' | 'skipped';
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  screenshotS3Key: string | null;
  metadataJson: WorkflowRunStepMetadataDto | null;
  createdAt: string;
};

/**
 * The Sandboxes v2 copy a run claimed (`workflow_run_detail_response.ts`'s
 * `sandboxV2Sandbox`). Exported standalone so other consumers of this shape
 * (e.g. `SandboxV2ToolbarButton`'s `currentRun`) import it rather than
 * redeclaring it.
 */
export type WorkflowRunSandboxV2Dto = {
  id: string;
  status: string;
  /**
   * The copy's last claim stage (`sandbox_v2_sandboxes.stage`); null before the
   * claim job first runs. Kept after the copy is ready — `status` supersedes it.
   */
  stage: SandboxV2CopyStage | null;
  /** Per-phase timings (snapshot wait, restore, prepare), ms; null until the copy is ready. */
  phases: { snapshotMs: number; restoreMs: number; prepareMs: number } | null;
  error: string | null;
};

export type WorkflowRunWithStepsDto = {
  id: string;
  workflowId: string;
  orgId: string;
  status: 'queued' | 'running' | 'success' | 'failed' | 'inconclusive' | 'canceled';
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  metadataJson: Record<string, unknown> | null;
  createdAt: string;
  steps: WorkflowRunStepDto[];
  /** S3 storage key for video recording of this run */
  videoStorageKey: string | null;
  /** Action timeline for video overlay (timestamps + actions) */
  videoTimelineJson: Record<string, unknown> | null;
  /** S3 key for the condensed "fast" video variant (null = no variant) */
  fastVideoStorageKey?: string | null;
  /** Remapped timeline + cut-interval map for the fast variant (FastVideoTimeline) */
  fastVideoTimelineJson?: Record<string, unknown> | null;
  /** S3 storage key for Playwright trace file */
  traceStorageKey: string | null;
  /** True when video segments exist but consolidated video is not yet ready */
  hasVideoSegments?: boolean;
  /** Published snapshot version (null for draft / legacy runs) */
  snapshotVersion?: number | null;
  /** Draft snapshot info (null for published / legacy runs) */
  draftSnapshot?: {
    id: string;
    version: string;
    basePublishedVersion: number;
  } | null;
  /** Immutable numbered revision pinned by canonical runs. */
  workflowRevision?: { id: string; number: number } | null;
  /**
   * The stable environment this run targeted (name + type). For sandbox runs
   * this is the persistent sandbox env, not the throwaway instance.
   */
  environment?: { id: string; name: string; type: string } | null;
  /** The ephemeral sandbox instance the run physically ran on (sandbox only). */
  environmentInstance?: {
    id: string;
    publicUrl: string | null;
    status: string;
    expiresAt: string | null;
  } | null;
  /** The Sandboxes v2 copy this run claimed, when its target environment is v2-backed. */
  sandboxV2Sandbox?: WorkflowRunSandboxV2Dto | null;
  execution?: {
    workflowRunId: string;
    testRunId: string | null;
    suiteExecutionId: string | null;
    triggerSource: string | null;
    targetEnvironment: { id: string; name: string; type: string } | null;
    runtimeEnvironment: { id: string; name: string; type: string } | null;
    sandbox: {
      id: string;
      name: string;
      templateId: string;
      status: string;
      publicUrl: string | null;
      liveUrl: string | null;
      createdAt: string;
      startedAt: string | null;
      expiresAt: string;
      terminatedAt: string | null;
    } | null;
  };
};

type WorkflowRunResponse = {
  ok: true;
  data: { run: WorkflowRunWithStepsDto };
};

type WorkflowRunRefetchState = Pick<
  WorkflowRunWithStepsDto,
  | 'status'
  | 'finishedAt'
  | 'videoStorageKey'
  | 'fastVideoStorageKey'
  | 'fastVideoTimelineJson'
  | 'hasVideoSegments'
>;

const WORKFLOW_RUN_POLL_INTERVAL_MS = 5000;
const FAST_VIDEO_SLOW_POLL_INTERVAL_MS = 30_000;
const FAST_VIDEO_FAST_POLL_WINDOW_MS = 60_000;
const FAST_VIDEO_MAX_POLL_WINDOW_MS = 15 * 60_000;

/**
 * Return the next detail refresh interval for a workflow run.
 *
 * Fast-video generation runs after the full recording is persisted, so a
 * terminal run with a full video can still acquire its fast-video pair later.
 * The bounded derivative poll keeps that pair visible without polling old
 * completed runs forever.
 */
export function getWorkflowRunRefetchInterval(
  run: WorkflowRunRefetchState | null | undefined,
  now = Date.now()
): number | false {
  if (!run) return false;

  // Keep active run details fresh so wait-node resume state does not pin the
  // live browser panel to a stale waiting snapshot.
  if (run.status === 'queued' || run.status === 'running') {
    return WORKFLOW_RUN_POLL_INTERVAL_MS;
  }

  // Poll while video is processing (segments exist but consolidated video not
  // ready). This is also the safety guard for runs that never produce a full
  // recording.
  if (run.hasVideoSegments && !run.videoStorageKey) {
    return WORKFLOW_RUN_POLL_INTERVAL_MS;
  }

  const fastVideoReady = Boolean(run.fastVideoStorageKey && run.fastVideoTimelineJson);
  if (run.videoStorageKey && !fastVideoReady) {
    const finishedAtMs = run.finishedAt ? Date.parse(run.finishedAt) : Number.NaN;
    if (!Number.isFinite(finishedAtMs)) return false;

    const ageMs = now - finishedAtMs;
    if (ageMs < FAST_VIDEO_FAST_POLL_WINDOW_MS) {
      return WORKFLOW_RUN_POLL_INTERVAL_MS;
    }
    if (ageMs < FAST_VIDEO_MAX_POLL_WINDOW_MS) {
      return FAST_VIDEO_SLOW_POLL_INTERVAL_MS;
    }
  }

  return false;
}

export function useWorkflowRun(workflowId: string | null, runId: string | null) {
  const [wrongOrgInfo, setWrongOrgInfo] = React.useState<WrongOrgInfo | null>(null);

  const query = useQuery({
    queryKey: ['workflow-run', workflowId, runId],
    enabled: Boolean(workflowId && runId),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.details?.error === 'WORKFLOW_WRONG_ORG') {
        return false;
      }
      return failureCount < 2;
    },
    queryFn: async () => {
      if (!workflowId || !runId) throw new Error('Missing workflowId or runId');
      const r = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}`, {
        credentials: 'include',
      });
      if (!r.ok) {
        const apiError = await toApiError(r, 'Failed to fetch workflow run');
        const details = apiError.details;
        if (
          details?.error === 'WORKFLOW_WRONG_ORG' &&
          typeof details.targetOrgId === 'string' &&
          typeof details.targetOrgName === 'string'
        ) {
          setWrongOrgInfo({ orgId: details.targetOrgId, orgName: details.targetOrgName });
        }
        throw apiError;
      }
      setWrongOrgInfo(null);
      const data = (await r.json()) as WorkflowRunResponse;
      if (!data.ok) throw new Error('Failed to fetch workflow run');
      return data;
    },
    refetchInterval: (query) => getWorkflowRunRefetchInterval(query.state.data?.data?.run),
  });

  const clearWrongOrgInfo = React.useCallback(() => setWrongOrgInfo(null), []);

  return { ...query, wrongOrgInfo, clearWrongOrgInfo };
}
